/**
 * Core Animation Engine for Spirit of Shabbat loader
 *
 * Provides:
 * - requestAnimationFrame loop with delta time
 * - Easing functions for organic animations
 * - Canvas resize handling
 * - Animation state machine (intro, loop, exit)
 * - Module registration system for extensibility
 */

import type {
  AnimationModule,
  AnimationEngineConfig,
  AnimationPhase,
  OnPhaseChange,
  OnComplete,
} from '../types';

function clearAndResizeCanvas(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): boolean {
  const rect = canvas.getBoundingClientRect();
  const width = rect.width > 0 ? rect.width : canvas.width;
  const height = rect.height > 0 ? rect.height : canvas.height;
  if (width <= 0 || height <= 0) return false;
  ctx.clearRect(0, 0, width, height);
  return true;
}

function updateAndRenderModules(
  modules: Map<string, AnimationModule>,
  ctx: CanvasRenderingContext2D,
  deltaTime: number,
  phase: AnimationPhase,
  phaseProgress: number
): void {
  for (const module of modules.values()) {
    module.update(deltaTime, phase, phaseProgress);
    module.render(ctx);
  }
}

interface AnimationCompletionParams {
  hasCompleted: boolean;
  markComplete: () => void;
  onComplete: (() => void) | null;
}

function handleAnimationCompletion(params: AnimationCompletionParams): void {
  if (params.hasCompleted) return;
  params.markComplete();
  params.onComplete?.();
}

// Default configuration
const DEFAULT_CONFIG: Required<AnimationEngineConfig> = {
  targetFPS: 60,
  introMinDuration: 1500,
  loopPauseDuration: 2000, // 2 second pause in loop before exit
  exitDuration: 500, // Fast fade out to site
  respectReducedMotion: true,
};

/**
 * Collection of easing functions for organic, natural animations
 */
export const Easing = {
  // Linear (no easing)
  linear: (t: number): number => t,

  // Cubic easing
  easeInCubic: (t: number): number => t * t * t,
  easeOutCubic: (t: number): number => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,

  // Sine easing (smooth, organic feel)
  easeInSine: (t: number): number => 1 - Math.cos((t * Math.PI) / 2),
  easeOutSine: (t: number): number => Math.sin((t * Math.PI) / 2),
  easeInOutSine: (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2,

  // Quadratic easing
  easeInQuad: (t: number): number => t * t,
  easeOutQuad: (t: number): number => 1 - (1 - t) * (1 - t),
  easeInOutQuad: (t: number): number => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),

  // Exponential easing
  easeOutExpo: (t: number): number => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  easeInExpo: (t: number): number => (t === 0 ? 0 : Math.pow(2, 10 * t - 10)),

  // Elastic easing (bouncy)
  easeOutElastic: (t: number): number => {
    const c4 = (2 * Math.PI) / 3;
    const elasticValue = t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    return t === 0 ? 0 : elasticValue;
  },

  // Back easing (overshoot)
  easeOutBack: (t: number): number => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },

  // Smooth step (S-curve)
  smoothStep: (t: number): number => t * t * (3 - 2 * t),
  smootherStep: (t: number): number => t * t * t * (t * (t * 6 - 15) + 10),
} as const;

/**
 * Simple Perlin-style noise for organic flame flicker
 * Based on a seeded random approach for deterministic results
 */
export function noise1D(x: number, seed = 0): number {
  const n = Math.sin(x * 12.9898 + seed * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

/**
 * Interpolated noise for smoother animations
 */
export function smoothNoise(x: number, seed = 0): number {
  const i = Math.floor(x);
  const f = x - i;
  const a = noise1D(i, seed);
  const b = noise1D(i + 1, seed);
  const t = f * f * (3 - 2 * f); // Smooth interpolation
  return a + (b - a) * t;
}

/**
 * Multi-octave noise for more natural variation
 */
export function fbmNoise(x: number, octaves = 3, seed = 0): number {
  let value = 0;
  let amplitude = 1;
  let frequency = 1;
  let maxValue = 0;

  for (let i = 0; i < octaves; i++) {
    value += smoothNoise(x * frequency, seed + i) * amplitude;
    maxValue += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }

  return value / maxValue;
}

/**
 * Main Animation Engine class
 */
export class AnimationEngine {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private modules: Map<string, AnimationModule> = new Map();
  private config: Required<AnimationEngineConfig>;

  // Animation state
  private phase: AnimationPhase = 'intro';
  private isRunning = false;
  private animationFrameId: number | null = null;

  // Timing
  private startTime = 0;
  private lastFrameTime = 0;
  private introStartTime = 0;
  private loopStartTime = 0;
  private exitStartTime = 0;
  private phaseProgress = 0;

  // Callbacks
  private onPhaseChange: OnPhaseChange | null = null;
  private onComplete: OnComplete | null = null;

  // Reduced motion
  private prefersReducedMotion = false;

  // Ready state (from external source like auth)
  private externalReady = false;

  // Failsafe timeout to prevent infinite loading (30 seconds max)
  private failsafeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private static readonly FAILSAFE_TIMEOUT_MS = 30000;

  // Exit completion timeout - ensures completion fires even if rAF is throttled
  // Safari can throttle/pause rAF during opacity transitions on the container,
  // which would prevent the exit phase from reaching completion via the animation loop.
  private exitCompletionTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Track if we've already completed to prevent double-completion
  private hasCompleted = false;

  constructor(config: AnimationEngineConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Check for reduced motion preference
    if (this.config.respectReducedMotion && typeof window !== 'undefined') {
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  /**
   * Initialize the engine with a canvas element
   */
  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    if (!this.ctx) {
      const errorMsg = 'Could not get 2D context from canvas';
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    // Set up canvas size
    this.resize();

    // Initialize all registered modules with CSS dimensions (not DPR-scaled)
    const rect = canvas.getBoundingClientRect();
    for (const module of this.modules.values()) {
      module.init(this.ctx, rect.width, rect.height);
    }

    // Listen for resize events
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }
  }

  /**
   * Handle window resize
   */
  private handleResize = (): void => {
    this.resize();
  };

  /**
   * Resize canvas to match container
   */
  resize(): void {
    if (!this.canvas || !this.ctx) return;

    // Get device pixel ratio for sharp rendering
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    // Get container dimensions
    const rect = this.canvas.getBoundingClientRect();

    // Set canvas size accounting for DPR
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    // Scale context for DPR
    this.ctx.scale(dpr, dpr);

    // Notify modules of resize
    for (const module of this.modules.values()) {
      module.resize(rect.width, rect.height);
    }
  }

  /**
   * Register an animation module
   */
  registerModule(module: AnimationModule): void {
    this.modules.set(module.id, module);

    // Initialize if engine is already running
    if (this.ctx && this.canvas) {
      const rect = this.canvas.getBoundingClientRect();
      module.init(this.ctx, rect.width, rect.height);
    }
  }

  /**
   * Unregister an animation module
   */
  unregisterModule(id: string): void {
    const module = this.modules.get(id);
    if (module) {
      module.destroy();
      this.modules.delete(id);
    }
  }

  /**
   * Set callback for phase changes
   */
  setOnPhaseChange(callback: OnPhaseChange): void {
    this.onPhaseChange = callback;
  }

  /**
   * Set callback for animation complete
   */
  setOnComplete(callback: OnComplete): void {
    this.onComplete = callback;
  }

  /**
   * Signal that external resources are ready (e.g., auth loaded)
   */
  setExternalReady(ready: boolean): void {
    this.externalReady = ready;
  }

  /**
   * Start the animation loop
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.hasCompleted = false;
    this.startTime = performance.now();
    this.introStartTime = this.startTime;
    this.lastFrameTime = this.startTime;
    this.phase = 'intro';

    this.onPhaseChange?.(this.phase);

    // Set up failsafe timeout to prevent infinite loading
    // This ensures the app loads even if something goes wrong with the animation
    this.failsafeTimeoutId = setTimeout(() => {
      if (!this.hasCompleted && this.isRunning) {
        console.warn(
          '[AnimationEngine] Failsafe timeout triggered - forcing completion after',
          AnimationEngine.FAILSAFE_TIMEOUT_MS,
          'ms'
        );
        this.forceComplete();
      }
    }, AnimationEngine.FAILSAFE_TIMEOUT_MS);

    this.loop();
  }

  /**
   * Force completion of the animation (used by failsafe)
   */
  private forceComplete(): void {
    if (this.hasCompleted) return;

    this.hasCompleted = true;
    this.phase = 'complete';
    this.phaseProgress = 1;
    this.isRunning = false;

    // Cancel any pending animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear failsafe timeout if it hasn't fired yet
    if (this.failsafeTimeoutId !== null) {
      clearTimeout(this.failsafeTimeoutId);
      this.failsafeTimeoutId = null;
    }

    // Clear exit completion timeout
    if (this.exitCompletionTimeoutId !== null) {
      clearTimeout(this.exitCompletionTimeoutId);
      this.exitCompletionTimeoutId = null;
    }

    this.onPhaseChange?.(this.phase);
    this.onComplete?.();
  }

  /**
   * Stop the animation loop
   */
  stop(): void {
    this.isRunning = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear failsafe timeout
    if (this.failsafeTimeoutId !== null) {
      clearTimeout(this.failsafeTimeoutId);
      this.failsafeTimeoutId = null;
    }

    // Clear exit completion timeout
    if (this.exitCompletionTimeoutId !== null) {
      clearTimeout(this.exitCompletionTimeoutId);
      this.exitCompletionTimeoutId = null;
    }
  }

  /**
   * Trigger the exit animation
   */
  triggerExit(): void {
    if (this.phase === 'exit' || this.phase === 'complete') return;

    this.phase = 'exit';
    this.exitStartTime = performance.now();
    this.phaseProgress = 0;

    // Notify modules to start exit animation
    for (const module of this.modules.values()) {
      module.triggerExit?.();
    }

    this.onPhaseChange?.(this.phase);

    // Set a timeout-based fallback for exit completion.
    // Safari can throttle or pause requestAnimationFrame during CSS opacity
    // transitions on the parent container, which would prevent the rAF loop
    // from advancing the exit phase to completion. This timeout ensures
    // completion fires even if rAF stops updating.
    if (this.exitCompletionTimeoutId !== null) {
      clearTimeout(this.exitCompletionTimeoutId);
    }
    this.exitCompletionTimeoutId = setTimeout(() => {
      this.exitCompletionTimeoutId = null;
      if (!this.hasCompleted && this.phase === 'exit') {
        this.forceComplete();
      }
    }, this.config.exitDuration + 200); // exitDuration + small buffer
  }

  /**
   * Get current phase
   */
  getPhase(): AnimationPhase {
    return this.phase;
  }

  /**
   * Get phase progress (0-1)
   */
  getProgress(): number {
    return this.phaseProgress;
  }

  /**
   * Check if reduced motion is preferred
   */
  isReducedMotion(): boolean {
    return this.prefersReducedMotion;
  }

  /**
   * Main animation loop
   */
  private loop = (): void => {
    if (!this.isRunning || !this.ctx || !this.canvas || this.hasCompleted) return;

    try {
      const currentTime = performance.now();
      const deltaTime = Math.min(currentTime - this.lastFrameTime, 100);
      this.lastFrameTime = currentTime;

      this.updatePhase(currentTime);

      const cleared = clearAndResizeCanvas(this.ctx, this.canvas);
      if (cleared) {
        updateAndRenderModules(this.modules, this.ctx, deltaTime, this.phase, this.phaseProgress);
      }

      if (this.phase !== 'complete') {
        this.animationFrameId = requestAnimationFrame(this.loop);
      } else {
        handleAnimationCompletion({
          hasCompleted: this.hasCompleted,
          markComplete: () => {
            this.hasCompleted = true;
            this.isRunning = false;
            if (this.failsafeTimeoutId !== null) {
              clearTimeout(this.failsafeTimeoutId);
              this.failsafeTimeoutId = null;
            }
            if (this.exitCompletionTimeoutId !== null) {
              clearTimeout(this.exitCompletionTimeoutId);
              this.exitCompletionTimeoutId = null;
            }
          },
          onComplete: this.onComplete,
        });
      }
    } catch (error) {
      console.error('[AnimationEngine] Error in animation loop, forcing completion:', error);
      this.forceComplete();
    }
  };

  /**
   * Update animation phase based on timing and ready state
   */
  private updatePhase(currentTime: number): void {
    switch (this.phase) {
      case 'intro': {
        const introElapsed = currentTime - this.introStartTime;
        this.phaseProgress = Math.min(introElapsed / this.config.introMinDuration, 1);

        // Transition to loop when intro is complete
        if (introElapsed >= this.config.introMinDuration) {
          this.phase = 'loop';
          this.loopStartTime = currentTime;
          this.phaseProgress = 0;
          this.onPhaseChange?.(this.phase);
        }
        break;
      }

      case 'loop': {
        const loopElapsed = currentTime - this.loopStartTime;

        // Wait for minimum pause duration before allowing exit
        const canExit = loopElapsed >= this.config.loopPauseDuration;

        // In loop phase, check if external resources are ready AND pause is complete
        if (this.externalReady && canExit) {
          this.triggerExit();
        }
        // Progress oscillates for breathing animations
        this.phaseProgress = (Math.sin(currentTime / 1000) + 1) / 2;
        break;
      }

      case 'exit': {
        const exitElapsed = currentTime - this.exitStartTime;
        this.phaseProgress = Math.min(exitElapsed / this.config.exitDuration, 1);

        // Transition to complete when exit is done
        if (exitElapsed >= this.config.exitDuration) {
          this.phase = 'complete';
          this.phaseProgress = 1;
          this.onPhaseChange?.(this.phase);
        }
        break;
      }

      case 'complete':
        // Nothing to do
        break;
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stop();

    // Clear failsafe timeout
    if (this.failsafeTimeoutId !== null) {
      clearTimeout(this.failsafeTimeoutId);
      this.failsafeTimeoutId = null;
    }

    // Clear exit completion timeout
    if (this.exitCompletionTimeoutId !== null) {
      clearTimeout(this.exitCompletionTimeoutId);
      this.exitCompletionTimeoutId = null;
    }

    // Destroy all modules
    for (const module of this.modules.values()) {
      module.destroy();
    }
    this.modules.clear();

    // Remove event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }

    this.canvas = null;
    this.ctx = null;
  }
}

// Export a utility for creating interpolated values
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

// Clamp a value between min and max
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// Map a value from one range to another
export function mapRange(
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
): number {
  return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}
